// Team-library view: teams → songs grouped by songId → versions → navigate.
// Strings localized via i18n (English default, RU parity). Pure render; navigation injected.

import type { TeamLibrary, LibraryItem } from '../library.js';
import type { ViewerInfo, ShareRemoveResult } from '../shares-api.js';
import type { SongTextsBackfillResult, SongTextsBackfillReport } from '../song-texts-backfill-api.js';
import type { SongTextWriteResult } from '../song-text-api.js';
import { t, tn, getLocale } from './i18n.js';
import { splitAndSortSetlists } from './setlist-sort.js';
import { librarySetlistEntries, setlistEntryPath, type LibrarySetlistEntry } from './library-setlists.js';
import { buildSongRows, sortSongRows, filterSongRows, type SongRow, type SongSort } from './song-rows.js';
import { buildSongRowControls } from './song-row-controls.js';
import { openSongSheet } from './song-sheet.js';
import { buildSongSheetWords } from './song-sheet-words.js';
import { copyText } from './copy-text.js';
import { buildSongSheetRecordings } from './song-sheet-recordings.js';
import { buildSongSheetViewers, type SongSheetViewer } from './song-sheet-viewers.js';
import { saveWordsNeighbors, neighborsFromRows, saveWordsOrigin } from '../song-words-view.js';
import { buildSortControl } from './sort-control.js';
import { formatServiceDate, formatShortDate, relativeHint } from './format-date.js';
import { icon } from './icons.js';
import { buildEmptyState } from './empty-state.js';
import { renderFreshnessLine } from './freshness-line.js';
import type { FreshnessState } from '../offline/freshness.js';
// DS segmented control for the Setlists/Songs tab bar (registers the custom
// element; its light-DOM CSS must be imported alongside).
import '@bandway/ui-components';
import '@bandway/ui-components/styles/bw-segmented-control.css';
// Тег «Words» у ссылки слов без записи — единый сетлист, фаза 1, задача 11.
import '@bandway/ui-components/styles/bw-tag.css';

interface LibraryViewOpts {
  teams: TeamLibrary[];
  navigate: (path: string) => void;
  meEmail?: string | null;
  /** Owner-only storage usage for the current team; null hides the chip. Data from the server. */
  quota?: { used: number; limit: number } | null;
  loadViewers?: (shareId: string) => Promise<{ ok: true; viewers: ViewerInfo[] } | { ok: false; error: string }>;
  /** Current visibility of a version, to pre-select the toggle. null = unknown/not owned. */
  loadVisibility?: (shareId: string) => Promise<'public' | 'team' | null>;
  revokeViewer?: (shareId: string, userId: string) => Promise<{ ok: boolean }>;
  setVisibility?: (shareId: string, visibility: 'public' | 'team', teamId: string | null) => Promise<{ ok: boolean }>;
  /**
   * Wave C completeness pass: the first `/api/library` fetch is in flight —
   * render a ghost skeleton instead of real content. `teams` is ignored while
   * this is true (the caller has nothing real to pass yet).
   */
  loading?: boolean;
  /**
   * Wave C completeness pass: the most recent library refresh failed. Read
   * together with `teams` (the last known-good list, or `[]` if nothing has
   * ever loaded successfully):
   *  - `teams.length === 0` → nothing has ever loaded — render the full
   *    "no connection" explanation in place of the normal view.
   *  - `teams.length > 0` → a list is already on screen — render it exactly
   *    as a successful load would (unchanged), with a connection-lost banner
   *    on top. The rows are NEVER cleared because a refresh failed; see the
   *    "отказ обновления НЕ стирает список" invariant (Wave C task brief).
   * The caller (main.ts) owns retaining the last-good `teams` in memory —
   * this view never fetches or caches anything itself.
   */
  fetchFailed?: boolean;
  /** Retry action for the offline / connection-lost states above. A message
   * without an action is treated as an unclosed state in this task — always
   * pass this when `fetchFailed` is true. */
  onRetry?: () => void;
  /**
   * Wave D task 6: runs the owner's one-shot words backfill
   * (`POST /api/song-texts/backfill` — self-only, idempotent). Wired by the
   * caller the same way `loadViewers`/`setVisibility` are — this view never
   * imports the network client directly. The action that calls this lives on
   * the "no words anywhere" notice (buildNoWordsNotice) and is shown ONLY
   * when `quota` is present — the SAME owner-recognition signal the quota
   * chip and the viewers panel already use, because the route itself only
   * ever reads the SIGNED-IN account's own live text-share links: a
   * non-owner would see a button promising something it can never do.
   */
  backfillWords?: () => Promise<SongTextsBackfillResult>;
  /**
   * Called once after a backfill run actually added or refreshed at least
   * one song's words — the caller's cue to refetch `/api/library` and
   * remount, so the newly-imported songs show up without a page reload.
   * Never called for a zero-result run (nothing changed, nothing to redraw).
   */
  onWordsImported?: () => void;
  /**
   * Волна F, задача 7 — запись по СЛОВАМ песни: `PATCH /api/song-texts/:id`
   * (кто их видит) и `DELETE` (снять с публикации). Проводка, как у
   * `setVisibility`/`revokeViewer` выше: вьюха не импортирует сетевой клиент
   * сама.
   *
   * Итог — `SongTextWriteResult`, а не `{ ok }` как у соседей: раздел обязан
   * НАЗВАТЬ причину отказа («войдите заново» / «это не ваши слова» / «их уже
   * нет» / «сервер не ответил»), и схлопни причину в булево здесь — назвать её
   * будет уже неоткуда.
   *
   * Обе НЕОБЯЗАТЕЛЬНЫЕ, и отсутствие любой из них убирает раздел слов из листа
   * целиком (см. buildSongSheetControl). Это не перестраховка, а то же
   * правило, что уже держит кнопку переноса слов: орган, который ничего не
   * делает, хуже отсутствующего, а условие в DOM делает пропавшую проводку
   * видимой тесту, а не только человеку в бою.
   */
  setWordsVisibility?: (id: string, visibility: 'team' | 'public') => Promise<SongTextWriteResult>;
  removeWords?: (id: string) => Promise<SongTextWriteResult>;
  /**
   * Слова сняты с публикации — вызывающему пора перезапросить `/api/library` и
   * перемонтировать: строка обязана потерять слова сразу, без перезагрузки
   * страницы. Тем же приёмом, что `onWordsImported` выше.
   */
  onWordsRemoved?: () => void;
  /**
   * Волна F: снять ЗАПИСЬ с публикации — `DELETE /api/shares/:shareId`
   * (shares-api.removeShare). Проводка, как у соседей выше: вьюха не
   * импортирует сетевой клиент сама.
   *
   * НЕОБЯЗАТЕЛЬНАЯ, и её отсутствие убирает из раздела записей само действие
   * «убрать» (но не сам раздел). То же правило, что держит кнопку переноса
   * слов: орган, который ничего не делает, хуже отсутствующего.
   */
  removeVersion?: (shareId: string) => Promise<ShareRemoveResult>;
  /**
   * Запись снята — вызывающему пора перезапросить `/api/library` и
   * перемонтировать: снятая запись обязана уйти из списка сразу, без
   * перезагрузки страницы. Тем же приёмом, что `onWordsRemoved` выше.
   */
  onVersionRemoved?: () => void;
  /**
   * Состояние офлайн-кэша (волна G). НЕОБЯЗАТЕЛЬНОЕ по той же причине, что
   * `setVisibility`/`removeWords` рядом: орган гаснет вместе со своей
   * проводкой. Не передали — строки нет, и вызовы, не знающие про офлайн
   * (скелет загрузки, тесты соседних задач), продолжают работать как прежде.
   */
  freshness?: FreshnessState;
  /** Локаль для формата времени в строке. По умолчанию — текущая. */
  freshnessLocale?: string;
}

const QUOTA_WARN_PCT = 80;
const chipMb = (n: number): number => Math.round(n / (1024 * 1024));

// ── return-from-words scroll restore (Wave C review fix, point 7) ─────────
// Spec promise: "Closing returns to the library, on the same tab AND the
// same place in the list." The tab already survives (song-words-view.ts's
// onClose writes 'bw.lib.tab' before navigating); the scroll position did
// not — onClose does a REAL navigation (`navigate('/library')` →
// `location.assign`, a full reload of the route per song-words-view.ts's own
// header comment), so there is no in-memory list to just... not throw away.
// Same bridge as the ‹› neighbors: sessionStorage, written right before
// leaving (song body click) and consumed on the very next library mount —
// one read, not a lingering "always jump to this song" setting.
const SCROLL_TARGET_KEY = 'bw.lib.scrollTo';

/** Сквозной счётчик id списков версий: `aria-controls` требует id, а не ключ. */
let versionsSeq = 0;

/** Remembers `row.key` right before navigating away to its words page. */
function saveScrollTarget(key: string): void {
  try { sessionStorage.setItem(SCROLL_TARGET_KEY, key); } catch { /* приватный режим */ }
}

/** Reads and EATS the remembered row key. Throwing storage (private mode) → null, not a crash. */
function readAndClearScrollTarget(): string | null {
  try {
    const key = sessionStorage.getItem(SCROLL_TARGET_KEY);
    sessionStorage.removeItem(SCROLL_TARGET_KEY);
    return key;
  } catch {
    return null;
  }
}

/** Compact storage-usage chip for the library header (owner only). Amber from 80%, red at 100%. */
export function renderQuotaChip(q: { used: number; limit: number }): HTMLElement {
  const pct = q.limit > 0 ? (q.used / q.limit) * 100 : 0;
  const chip = document.createElement('span');
  chip.dataset.role = 'quota-chip';
  chip.className = 'quota-chip' + (pct >= 100 ? ' is-full' : pct >= QUOTA_WARN_PCT ? ' is-warn' : '');
  chip.textContent = `${chipMb(q.used)}/${chipMb(q.limit)} MB`;
  return chip;
}

// SongGroup + groupBySong live in song-rows.ts (Wave C) so the rule "what
// counts as one song" for audio has exactly one home; song-rows.ts also
// stitches in words publications via buildSongRows(), which this file's
// songs panel now renders (SongGroup/groupBySong are no longer imported
// here directly — buildSongRows composes them internally).

/**
 * Build and wire a viewers panel for a given share version.
 * Returns the panel element (hidden initially, toggled by the btn).
 */
function buildViewersPanel(
  shareId: string,
  teamId: string,
  opts: LibraryViewOpts,
): HTMLDivElement {
  const panel = document.createElement('div');
  panel.dataset.role = 'viewers-panel';
  panel.dataset.shareId = shareId;

  // Visibility — a DS segmented control (Public | Team) whose CURRENT state is
  // pre-selected from the server. Two identical borderless buttons with no
  // selected state read as "непонятное состояние" (Сергей 2026-07-23); the
  // mutually-exclusive control + pre-selection fixes that. The note clarifies
  // this affordance is owner-only (answers "кто это видит").
  const visWrap = document.createElement('div');
  visWrap.dataset.role = 'vis-toggle';

  const visHint = document.createElement('span');
  visHint.dataset.role = 'vis-hint';
  visHint.textContent = t('library.visHint');
  visWrap.appendChild(visHint);

  const visSeg = document.createElement('bw-segmented-control') as HTMLElement & {
    segments: Array<{ value: string; label: string }>;
    value: string;
  };
  visSeg.dataset.role = 'vis-seg';
  visSeg.setAttribute('size', 'sm');
  visSeg.setAttribute('aria-label', t('library.visHint'));
  visSeg.segments = [
    { value: 'public', label: t('library.visPublic') },
    { value: 'team', label: t('library.visTeam') },
  ];
  visWrap.appendChild(visSeg);

  // Status line — reflects whether a visibility change saved or failed. aria-live
  // so the change is announced; avoids the silent fire-and-forget UX hole.
  const visStatus = document.createElement('span');
  visStatus.dataset.role = 'vis-status';
  visStatus.setAttribute('aria-live', 'polite');
  visWrap.appendChild(visStatus);

  const visNote = document.createElement('span');
  visNote.dataset.role = 'vis-note';
  visNote.textContent = t('library.visOwnerNote');
  visWrap.appendChild(visNote);

  panel.appendChild(visWrap);

  // Pre-select the current visibility (owner-only; async). Keep it as the
  // known-good value so a failed change can revert the control.
  let currentVis: 'public' | 'team' | null = null;
  Promise.resolve(opts.loadVisibility?.(shareId)).then(
    (vis) => { if (vis === 'public' || vis === 'team') { currentVis = vis; visSeg.value = vis; } },
    () => { /* leave unselected if the current state can't be read */ },
  );

  visSeg.addEventListener('bw-segmented-change', (e) => {
    const next = (e as CustomEvent<{ value: string }>).detail?.value === 'public' ? 'public' : 'team';
    if (!opts.setVisibility) return;
    visStatus.textContent = '';
    Promise.resolve(opts.setVisibility(shareId, next, teamId)).then(
      (res) => {
        if (res.ok) { currentVis = next; visStatus.textContent = t('library.visSaved'); }
        else { visSeg.value = currentVis ?? ''; visStatus.textContent = t('library.visError'); }
      },
      () => { visSeg.value = currentVis ?? ''; visStatus.textContent = t('library.visError'); },
    );
  });

  // Viewers list (populated async)
  const listEl = document.createElement('div');
  listEl.dataset.role = 'viewers-list';
  panel.appendChild(listEl);

  // Fetch and render viewers
  Promise.resolve(opts.loadViewers?.(shareId)).then((result) => {
    if (!result) return; // no callback injected
    listEl.innerHTML = '';
    if (!result.ok) {
      listEl.textContent = t('library.viewersError');
      return;
    }
    if (result.viewers.length === 0) {
      listEl.textContent = t('library.noViewers');
      return;
    }
    for (const viewer of result.viewers) {
      const row = document.createElement('div');
      row.dataset.role = 'viewer-row';
      row.dataset.userId = viewer.userId;

      const av = document.createElement('span');
      av.className = 'lib-avatar';
      av.textContent = (viewer.email.trim()[0] || '?').toLocaleUpperCase();
      row.appendChild(av);

      const lastSeen = viewer.lastSeenAt ? viewer.lastSeenAt.slice(0, 10) : t('library.lastSeenNever');
      const source = viewer.source === 'team' ? t('library.sourceTeam') : t('library.sourceGrant');
      const rowText = t('library.viewerRow', { email: viewer.email, lastSeen, source });
      const textSpan = document.createElement('span');
      textSpan.textContent = rowText;
      row.appendChild(textSpan);

      if (viewer.revokedAt !== null) {
        const revokedSpan = document.createElement('span');
        revokedSpan.textContent = t('library.revoked');
        row.appendChild(revokedSpan);
      } else {
        const revokeBtn = document.createElement('button');
        revokeBtn.dataset.role = 'revoke-btn';
        revokeBtn.dataset.userId = viewer.userId;
        revokeBtn.textContent = t('library.revokeBtn');
        revokeBtn.addEventListener('click', () => {
          if (!opts.revokeViewer) return;
          Promise.resolve(opts.revokeViewer(shareId, viewer.userId)).then((res) => {
            if (res.ok) {
              // Grey/remove the row on success
              row.style.opacity = '0.5';
              revokeBtn.remove();
              const greyedSpan = document.createElement('span');
              greyedSpan.textContent = t('library.revoked');
              row.appendChild(greyedSpan);
            }
          }, () => { /* network/throw — leave row actionable for retry */ });
        });
        row.appendChild(revokeBtn);
      }

      listEl.appendChild(row);
    }
  }, () => { listEl.innerHTML = ''; listEl.textContent = t('library.viewersError'); });

  return panel;
}

/**
 * Wave C completeness pass — ghost rows shown while `/api/library` is in
 * flight. The request travels over a phone's mobile connection in a venue
 * and can take a couple of seconds; before this, that gap was a blank page
 * (main.ts didn't render anything until the fetch resolved), which reads as
 * a crash rather than a load. `role="status"` + the aria-label announce the
 * state once for screen readers without any visible ghost text.
 */
export function buildLibrarySkeleton(): HTMLElement {
  const wrap = document.createElement('div');
  wrap.dataset.role = 'library-skeleton';
  wrap.className = 'lib-skeleton';
  wrap.setAttribute('role', 'status');
  wrap.setAttribute('aria-label', t('library.loading'));

  const titleBar = document.createElement('div');
  titleBar.className = 'lib-skeleton-bar lib-skeleton-title';
  wrap.appendChild(titleBar);

  const tabBar = document.createElement('div');
  tabBar.className = 'lib-skeleton-bar lib-skeleton-tabs';
  wrap.appendChild(tabBar);

  for (let i = 0; i < 5; i++) {
    const row = document.createElement('div');
    row.className = 'lib-skeleton-row';
    const name = document.createElement('span');
    name.className = 'lib-skeleton-bar is-name';
    const meta = document.createElement('span');
    meta.className = 'lib-skeleton-bar is-meta';
    row.appendChild(name);
    row.appendChild(meta);
    wrap.appendChild(row);
  }

  return wrap;
}

/**
 * Wave C completeness pass — a failed refresh must never clear an
 * already-shown list (the core invariant of this task): the rows stay
 * exactly as they were, and this strip appears above them instead, with the
 * same retry action the never-loaded offline state uses below.
 */
function buildConnectionLostBanner(onRetry?: () => void): HTMLElement {
  const bar = document.createElement('div');
  bar.dataset.role = 'connection-lost-banner';
  bar.className = 'lib-conn-banner';

  const ic = document.createElement('span');
  ic.className = 'lib-conn-icon';
  ic.innerHTML = icon('alert');
  bar.appendChild(ic);

  const text = document.createElement('span');
  text.className = 'lib-conn-text';
  text.textContent = t('library.connectionLostBanner');
  bar.appendChild(text);

  if (onRetry) {
    const btn = document.createElement('button');
    btn.dataset.role = 'connection-lost-retry';
    btn.className = 'lib-conn-retry';
    btn.textContent = t('library.retryBtn');
    btn.addEventListener('click', onRetry);
    bar.appendChild(btn);
  }

  return bar;
}

/**
 * Wave C completeness pass — shown above the songs list when EVERY row in
 * the team lacks words. Deliberately distinct from the "nothing published"
 * empty state (buildEmptyState/team-empty below): rows with recordings can
 * still exist here (Wave B/B2's whole point), so replacing the list with a
 * generic empty state would hide real audio. Without this notice a musician
 * has no way to know why every song tap goes nowhere — words are published
 * from the desktop app, not the web, and that's a team-wide fact here, not a
 * per-song one (the per-row `title` hint in buildSongEl explains the single
 * row; this explains the pattern across all of them).
 */
function buildNoWordsNotice(opts: LibraryViewOpts): HTMLElement {
  const box = document.createElement('div');
  box.dataset.role = 'no-words-notice';
  box.className = 'lib-notice';

  const ic = document.createElement('span');
  ic.className = 'lib-notice-icon';
  ic.innerHTML = icon('alert');
  box.appendChild(ic);

  const text = document.createElement('span');
  text.className = 'lib-notice-text';
  const title = document.createElement('strong');
  title.className = 'lib-notice-title';
  title.textContent = t('library.noWordsYet');
  const body = document.createElement('span');
  body.className = 'lib-notice-body';
  body.textContent = t('library.noWordsYetBody');
  text.appendChild(title);
  text.appendChild(body);
  box.appendChild(text);

  // Wave D task 6: the action lives on THIS notice, owner-only (see
  // LibraryViewOpts.backfillWords' own doc comment for why `quota` is the
  // right gate — same signal as the quota chip/viewers panel).
  // `backfillWords` в условии — не перестраховка, а починка настоящего
  // дефекта: без него кнопка рисовалась и при неподанной зависимости, а
  // нажатие молча возвращалось (гард стоял только в обработчике). Кнопка,
  // которая ничего не делает, хуже отсутствующей — это правило проекта. Заодно
  // условие делает замок на проводку в main.ts работающим: снятие зависимости
  // теперь видно в DOM, а не только в поведении.
  if (opts.quota && opts.backfillWords) {
    box.appendChild(buildImportWordsControl(opts));
  }

  return box;
}

/**
 * Which skip reason to NAME when a backfill run completed with zero songs
 * gaining words. The route's own report carries a full breakdown
 * (`skippedByReason` — see backfill.ts's doc comments for each code); a bare
 * "0" or, worse, a generic "done" would read as success to a musician who
 * just watched a spinner and got nothing. `fresher_record_exists` gets its
 * OWN phrasing on purpose: it means the words are already there (a REPEAT
 * run of an idempotent route), which is genuinely good news, not a problem —
 * collapsing it into the same "couldn't import" bucket as a real failure
 * would be a false alarm.
 */
function describeZeroImportReason(report: SongTextsBackfillReport): string {
  if (report.linksReviewed === 0) return t('library.importZeroNoLinks');

  let topReason: string | null = null;
  let topCount = 0;
  for (const [reason, count] of Object.entries(report.skippedByReason)) {
    if (count > topCount) { topCount = count; topReason = reason; }
  }

  switch (topReason) {
    case 'fresher_record_exists':
      return t('library.importZeroUpToDate');
    case 'no_team':
    case 'missing_song_ids':
      return t('library.importZeroOldLinks');
    case 'blob_missing':
    case 'blob_invalid':
      return t('library.importZeroDataGone');
    default:
      return t('library.importZeroGeneric');
  }
}

/**
 * The owner-only "Import words from my setlist links" button + its status
 * line, hung off buildNoWordsNotice above. Every branch of the run below
 * ends by re-enabling the button — a failed or zero-result run must stay
 * re-triggerable, only a genuine in-flight request blocks a second run.
 */
function buildImportWordsControl(opts: LibraryViewOpts): HTMLElement {
  const wrap = document.createElement('div');
  wrap.dataset.role = 'import-words';
  wrap.className = 'lib-import-words';

  const btn = document.createElement('button');
  btn.dataset.role = 'import-words-btn';
  btn.className = 'lib-import-btn';
  btn.textContent = t('library.importWordsBtn');
  wrap.appendChild(btn);

  const status = document.createElement('span');
  status.dataset.role = 'import-words-status';
  status.className = 'lib-import-status';
  status.setAttribute('aria-live', 'polite');
  wrap.appendChild(status);

  // The one guard the task's mutation gate targets directly: without it, a
  // fast double-tap on a phone fires the backfill twice concurrently.
  let running = false;

  const showRetry = (): void => {
    let retryBtn = wrap.querySelector('[data-role="import-words-retry"]') as HTMLButtonElement | null;
    if (!retryBtn) {
      retryBtn = document.createElement('button');
      retryBtn.dataset.role = 'import-words-retry';
      retryBtn.className = 'lib-import-retry';
      retryBtn.textContent = t('library.retryBtn');
      retryBtn.addEventListener('click', run);
      wrap.appendChild(retryBtn);
    }
  };

  // Сквозное ревью волны D (Important). `backfillSongTexts` различает
  // `reason: 'auth'` (401 — сессия истекла) от `reason: 'network'` ровно
  // затем, чтобы UI мог их различить: «повторить» на auth бьёт в тот же 401
  // до бесконечности, пока музыкант посреди служения не поймёт сам, что
  // нужно войти заново. Действие ведёт на /login тем же приёмом, что уже
  // используют needsAuth-ветка /library в main.ts и song-words-view.ts
  // (`deps.navigate('/login')`) — `opts.navigate` здесь та же функция.
  const showSignIn = (): void => {
    let signInBtn = wrap.querySelector('[data-role="import-words-signin"]') as HTMLButtonElement | null;
    if (!signInBtn) {
      signInBtn = document.createElement('button');
      signInBtn.dataset.role = 'import-words-signin';
      signInBtn.className = 'lib-import-signin';
      signInBtn.textContent = t('topbar.signIn');
      signInBtn.addEventListener('click', () => opts.navigate('/login'));
      wrap.appendChild(signInBtn);
    }
  };

  function run(): void {
    if (running || !opts.backfillWords) return;
    running = true;
    btn.disabled = true;
    wrap.querySelector('[data-role="import-words-retry"]')?.remove();
    wrap.querySelector('[data-role="import-words-signin"]')?.remove();
    status.dataset.tone = '';
    status.textContent = t('library.importWordsRunning');

    Promise.resolve(opts.backfillWords()).then(
      (result) => {
        running = false;
        btn.disabled = false;
        if (!result.ok) {
          status.dataset.tone = 'error';
          if (result.reason === 'auth') {
            status.textContent = t('library.importWordsAuthError');
            showSignIn();
          } else {
            status.textContent = t('library.importWordsError');
            showRetry();
          }
          return;
        }
        const gained = result.report.created + result.report.updated;
        if (gained > 0) {
          status.dataset.tone = '';
          status.textContent = t('library.importWordsSuccess', { n: gained });
          opts.onWordsImported?.();
        } else {
          status.dataset.tone = '';
          status.textContent = describeZeroImportReason(result.report);
        }
      },
      () => {
        // A throwing opts.backfillWords (should not happen — the client
        // itself never throws) never leaves the button stuck disabled.
        running = false;
        btn.disabled = false;
        status.dataset.tone = 'error';
        status.textContent = t('library.importWordsError');
        showRetry();
      },
    );
  }

  btn.addEventListener('click', run);
  return wrap;
}

export function mountLibraryView(host: HTMLElement, opts: LibraryViewOpts): void {
  host.innerHTML = '';
  const root = document.createElement('div');
  root.className = 'library-view';

  if (opts.loading) {
    root.appendChild(buildLibrarySkeleton());
    host.appendChild(root);
    return;
  }

  // ── Шапка экрана — ОДНА (волна E, задача 5) ───────────────────────────────
  // До этого экран нёс две шапки подряд: карточку приложения (app-header.ts —
  // логотип, аватар, меню аккаунта; общая для ВСЕХ маршрутов и потому
  // неприкосновенная) и сразу под ней собственную — надзаголовок «TEAM LIBRARY»
  // мелкими прописными над именем команды. На 375 px это отнимало верх экрана
  // дважды и оттягивало список вниз, а сам надзаголовок повторял то, что и так
  // стоит в подписи. Прототип (`header.topbar`, строка 433) держит одну: имя
  // команды — единственный заголовок экрана, под ним тонкая подпись
  // «Team library · 13 songs · 5 setlists» (`.sub`).
  const head = document.createElement('div');
  head.className = 'lib-head';

  // Ряд заголовка. При двух и более командах он же — орган выбора: шеврон
  // рядом с именем и невидимый <select>, растянутый поверх ряда (прототипная
  // `.team-btn` — кнопка с шевроном выбора). Переезжает МЕСТО и ВИД выбора,
  // механика остаётся нативной: пикер телефона достаётся даром, а
  // `[data-role="team-switcher"]` остаётся тем же <select> с событием change.
  const titleRow = document.createElement('div');
  titleRow.className = 'lib-title-row';
  const h = document.createElement('h1');   // team name — filled after selection
  titleRow.appendChild(h);
  head.appendChild(titleRow);

  const summary = document.createElement('p');
  summary.dataset.role = 'head-summary';
  summary.className = 'lib-head-sub';
  head.appendChild(summary);

  // Строка свежести офлайн-кэша — сразу под счётчиками: «сколько тут песен» и
  // «насколько это свежо» — один взгляд, а не два места на экране.
  if (opts.freshness) {
    head.appendChild(renderFreshnessLine(opts.freshness, opts.freshnessLocale ?? getLocale(), Date.now()));
  }

  // Owner-only storage chip (present only when the caller passes quota).
  if (opts.quota) head.appendChild(renderQuotaChip(opts.quota));
  root.appendChild(head);

  // "My team" lives only under the avatar menu now (Сергей 2026-07-02) — removed
  // the redundant in-page button.

  // Wave C completeness pass: the fetch failed and nothing has EVER loaded
  // (teams is empty because we have no last-known-good list, not because the
  // team genuinely has none) — a distinct explanation from the "no team"
  // empty state below, with a retry action. Must be checked BEFORE the
  // teams.length===0 branch below, which owns the legitimate empty case.
  if (opts.fetchFailed && opts.teams.length === 0) {
    // Имени команды здесь не существует: заголовок несёт общая строка, а
    // подпись прячется — считать нечего, и «0 songs · 0 setlists» читалось бы
    // как факт о библиотеке, которой мы ни разу не видели.
    h.textContent = t('library.title');
    summary.hidden = true;
    root.appendChild(buildEmptyState({
      icon: 'alert',
      title: t('library.offlineTitle'),
      body: t('library.offlineBody'),
      dataRole: 'offline',
      ...(opts.onRetry ? { action: { label: t('library.retryBtn'), onClick: opts.onRetry } } : {}),
    }));
    host.appendChild(root);
    return;
  }

  if (opts.teams.length === 0) {
    // No team → the generic title carries the header (no team name to show),
    // подпись прячется по той же причине, что и выше.
    h.textContent = t('library.title');
    summary.hidden = true;
    root.appendChild(buildEmptyState({ icon: 'users', title: t('library.empty'), dataRole: 'empty' }));
    host.appendChild(root);
    return;
  }

  // ── Team selection (persisted) ──
  const savedTeam = (() => { try { return localStorage.getItem('bw.lib.team'); } catch { return null; } })();
  let selectedTeam = opts.teams.find(t0 => t0.teamId === savedTeam) ?? opts.teams[0]!;
  h.textContent = selectedTeam.teamName;

  /**
   * Подпись шапки — счётчики ВЫБРАННОЙ команды. Функция, а не одно
   * присваивание, потому что нарисованная один раз подпись выглядит рабочей
   * ровно до второй команды: переключение оставило бы её на числах первой.
   * Единственный вызов — первой строкой `rerenderBody()`, через который
   * проходит и первый рендер (безусловный, ниже), и КАЖДАЯ смена команды.
   */
  const paintSummary = (): void => {
    // Счётчики приезжают в оболочку уже словами: английскому нужна форма
    // единственного числа («1 song»), и собрать её из одного шаблона с двумя
    // числами нечем.
    const songs = buildSongRows(selectedTeam).length;
    // Единый сетлист, фаза 1: считает и сетлисты со звуком, и ссылки слов без
    // пары — тем же источником, которым рисуется сама вкладка.
    const setlists = librarySetlistEntries(selectedTeam).length;
    summary.textContent = t('library.headSummary', {
      songs: tn('library.headSongs', songs),
      setlists: tn('library.headSetlists', setlists),
    });
  };

  // Wave C completeness pass: a list IS on screen (opts.teams is non-empty)
  // but the latest refresh failed — the core invariant of this task is that
  // this NEVER replaces the rows below; it only adds a banner above them.
  if (opts.fetchFailed) {
    root.appendChild(buildConnectionLostBanner(opts.onRetry));
  }

  // ── Tab selection (persisted) ──
  const savedTab = (() => { try { return localStorage.getItem('bw.lib.tab'); } catch { return null; } })();
  let activeTab: 'setlists' | 'songs' = savedTab === 'songs' ? 'songs' : 'setlists';

  // Выбор команды живёт НА ИМЕНИ и по-прежнему только при двух и более
  // командах: при одной выбирать не из чего, а меню из одного пункта обещает
  // выбор, которого нет.
  if (opts.teams.length > 1) {
    titleRow.classList.add('is-pickable');

    const chev = document.createElement('span');
    chev.className = 'lib-team-chev';
    chev.setAttribute('aria-hidden', 'true');   // имя органа несёт сам <select>
    chev.innerHTML = icon('chevronDown');
    titleRow.appendChild(chev);

    const sw = document.createElement('select');
    sw.dataset.role = 'team-switcher';
    sw.className = 'lib-team-switcher';
    sw.setAttribute('aria-label', t('library.teamSwitcherLabel'));
    for (const tm of opts.teams) {
      const o = document.createElement('option');
      o.value = tm.teamId; o.textContent = tm.teamName;
      if (tm.teamId === selectedTeam.teamId) o.selected = true;
      sw.appendChild(o);
    }
    sw.addEventListener('change', () => {
      selectedTeam = opts.teams.find(t0 => t0.teamId === sw.value) ?? selectedTeam;
      try { localStorage.setItem('bw.lib.team', selectedTeam.teamId); } catch { /* no localStorage */ }
      h.textContent = selectedTeam.teamName;
      rerenderBody();   // он же перерисовывает подпись со счётчиками — первой строкой
    });
    titleRow.appendChild(sw);
  }

  // ── Tab bar (DS segmented control) ──
  const tabs = document.createElement('bw-segmented-control') as HTMLElement & {
    segments: Array<{ value: string; label: string }>;
    value: string;
  };
  tabs.className = 'lib-tabs';
  tabs.setAttribute('size', 'md');
  tabs.setAttribute('aria-label', t('library.tabSetlists') + ' / ' + t('library.tabSongs'));
  root.appendChild(tabs);
  // Segment labels carry a live count ("Songs · 6"); rebuilt on every body
  // re-render so the counts track the selected team.
  const tabSegments = (): Array<{ value: string; label: string }> => [
    { value: 'setlists', label: `${t('library.tabSetlists')} · ${librarySetlistEntries(selectedTeam).length}` },
    // Row count, not audio-group count (Wave C): a team can have song rows made
    // entirely of words with zero recordings — counting groupBySong here would
    // show "Songs · 0" while the panel lists rows, the same inconsistency the
    // empty-state fix below addresses.
    { value: 'songs', label: `${t('library.tabSongs')} · ${buildSongRows(selectedTeam).length}` },
  ];

  // ── Panels ──
  // The per-team data-team-id marker lives on the songs panel so existing
  // section-scoped queries keep resolving after the rewrite.
  const setlistsPanel = document.createElement('div');
  setlistsPanel.dataset.role = 'panel-setlists'; setlistsPanel.className = 'lib-panel';
  const songsPanel = document.createElement('div');
  songsPanel.dataset.role = 'panel-songs'; songsPanel.className = 'lib-panel';
  root.appendChild(setlistsPanel);
  root.appendChild(songsPanel);

  const applyActive = (): void => {
    const setlistsOn = activeTab === 'setlists';
    setlistsPanel.hidden = !setlistsOn;
    songsPanel.hidden = setlistsOn;
    tabs.value = activeTab;   // the segmented control renders its own selected cap
  };
  const selectTab = (tab: 'setlists' | 'songs'): void => {
    activeTab = tab;
    try { localStorage.setItem('bw.lib.tab', tab); } catch { /* no localStorage */ }
    applyActive();
  };
  tabs.addEventListener('bw-segmented-change', (e) => {
    const v = (e as CustomEvent<{ value: string }>).detail?.value;
    selectTab(v === 'songs' ? 'songs' : 'setlists');
  });

  const rerenderBody = (): void => {
    paintSummary();   // счётчики шапки принадлежат выбранной команде, а не первой
    renderSetlistsPanel(setlistsPanel, selectedTeam, opts);
    renderSongsPanel(songsPanel, selectedTeam, opts);
    tabs.segments = tabSegments();   // refresh labels + live counts
    applyActive();
  };

  rerenderBody();
  host.appendChild(root);

  // Return-from-words scroll restore (point 7). Only reached past the
  // loading/offline/empty early-returns above — i.e. only when a real songs
  // list is actually on screen. Consumed unconditionally: a stale or foreign
  // key (row no longer on screen — deleted, filtered by a persisted search,
  // wrong team) simply finds nothing and no-ops, same as the ‹› neighbors
  // bridge does for a stale/foreign id.
  const scrollTarget = readAndClearScrollTarget();
  if (scrollTarget) {
    const row = root.querySelector(`[data-song-id="${scrollTarget}"]`) as HTMLElement | null;
    row?.scrollIntoView({ block: 'nearest' });
  }
}

/**
 * Build one setlist row: name over ONE meta line, plus a chevron.
 * The nearest upcoming row gets the `is-next` class.
 * Date is the eventDate when present, else createdAt (legacy / un-threaded).
 *
 * Волна E, задача 4 — почему мета стала одной строкой. До этого счётчик песен
 * был бейджем-пилюлей сразу после имени, дата уезжала вправо ЯНТАРНЫМ, а
 * относительная подсказка шла четвёртым элементом: четыре объекта в ряд, из
 * которых самым ярким был не тот, ради которого строку читают. Прототип
 * (`.row-setlist`, строка 176) кладёт имя и мету в колонку, а справа держит
 * только шеврон: «6 songs · Sun, Sep 21 · in 3 days» одной линией.
 *
 * Единый сетлист, фаза 1, задача 11 — строка теперь принимает
 * `LibrarySetlistEntry` (union сетлиста со звуком и ссылки слов без пары, см.
 * library-setlists.ts), а не только `SetlistSummary`. `dataset.kind` держит
 * разновидность (тесты и живой DOM на неё смотрят); `dataset.setlistId`
 * остаётся ТОЛЬКО у звука — старые тесты и живой DOM смотрят на него как на
 * признак «это сетлист со звуком». У ссылки слов перед шевроном встаёт
 * `bw-tag` с меткой «Words» — единственное видимое отличие строк в списке
 * (дизайн-решение владельца продукта, не отступать).
 */
function setlistRow(entry: LibrarySetlistEntry, opts: LibraryViewOpts, isNext: boolean): HTMLButtonElement {
  const row = document.createElement('button');
  row.type = 'button';
  row.dataset.role = 'setlist-row';
  row.dataset.kind = entry.kind;
  if (entry.kind === 'audio') row.dataset.setlistId = entry.setlistId;
  else row.dataset.textShareId = entry.textShareId;
  row.className = 'lib-setlist' + (isNext ? ' is-next' : '');

  const main = document.createElement('span');
  main.className = 'ls-main';

  const nameEl = document.createElement('span');
  nameEl.className = 'ls-name';
  nameEl.textContent = entry.name || t('library.untitled');
  main.appendChild(nameEl);

  // Одна метастрока вместо бейджа + даты + подсказки тремя отдельными
  // элементами: «6 songs · Sun, Sep 21 · in 3 days». Отсутствующий токен
  // пропускается, поэтому разделитель никогда не удваивается.
  const effective = entry.eventDate ?? entry.createdAt;
  const hint = relativeHint(effective, new Date());
  const tokens = [
    tn('library.setlistSongs', entry.itemCount),
    formatServiceDate(effective, getLocale()),
    hint ? t(hint.key, hint.n != null ? { n: hint.n } : {}) : null,
  ].filter((x): x is string => Boolean(x));

  const metaEl = document.createElement('span');
  metaEl.dataset.role = 'setlist-meta';
  metaEl.className = 'ls-sub';
  metaEl.textContent = tokens.join(' · ');
  main.appendChild(metaEl);

  row.appendChild(main);

  // Ссылка слов без записи несёт тег «Words» — у сетлиста со звуком его нет.
  if (entry.kind === 'words') {
    const tag = document.createElement('bw-tag');
    tag.setAttribute('size', 'sm');
    tag.textContent = t('library.wordsTag');
    row.appendChild(tag);
  }

  const chev = document.createElement('span');
  chev.className = 'ls-chev';
  chev.innerHTML = icon('chevronRight');
  row.appendChild(chev);

  row.addEventListener('click', () => opts.navigate(setlistEntryPath(entry)));
  return row;
}

/**
 * Setlists panel — a "Next" group (nearest service date first), then an
 * "Earlier" one (most recent first), each in its own plaque under its own
 * heading. Every row shows the name over ONE meta line (count · date ·
 * relative hint); the nearest upcoming row carries `is-next`. Empty state
 * shows `library.setlistsEmpty`.
 *
 * Единый сетлист, фаза 1, задача 11 — список строится из
 * `librarySetlistEntries(team)` (library-setlists.ts): сетлисты со звуком и
 * ссылки слов без пары одним списком, одна раскладка Next/Earlier на обе
 * разновидности (`splitAndSortSetlists` — дженерик по `eventDate ?? createdAt`,
 * ей всё равно, что за строка). Пустое состояние теперь смотрит на ОБЩИЙ
 * список записей, а не только на `team.setlists` — иначе команда с одними
 * ссылками слов и `setlists: []` увидела бы «нет сетлистов» поверх реального
 * списка.
 */
function renderSetlistsPanel(panel: HTMLElement, team: TeamLibrary, opts: LibraryViewOpts): void {
  panel.innerHTML = '';
  const entries = librarySetlistEntries(team);
  if (entries.length === 0) {
    panel.appendChild(buildEmptyState({ icon: 'musicOff', title: t('library.setlistsEmpty'), body: t('library.setlistsEmptyBody'), dataRole: 'setlists-empty' }));
    return;
  }

  const { upcoming, past } = splitAndSortSetlists(entries, new Date());

  /**
   * Одна группа = одна плашка под своей подписью. Подписаны ОБЕ (задача 4
   * волны E), поэтому ключа-подписи может не быть только у группы, которой не
   * существует: форма «плашка без подписи» убрана вместе с недостижимой
   * веткой — недостижимый код в этом проекте удаляется.
   */
  const group = (headingKey: string, dataRole: string, list: LibrarySetlistEntry[], markNext: boolean): void => {
    if (list.length === 0) return;
    const heading = document.createElement('div');
    heading.dataset.role = dataRole;
    heading.className = 'lib-sec';
    heading.textContent = t(headingKey);
    panel.appendChild(heading);
    const plaque = document.createElement('div');
    plaque.className = 'lib-plaque';
    for (let i = 0; i < list.length; i++) {
      plaque.appendChild(setlistRow(list[i]!, opts, markNext && i === 0));
    }
    panel.appendChild(plaque);
  };

  // Обе группы подписаны (прототип, строки 452 и 463: `Next` / `Earlier`).
  // `data-role` второй остаётся `past-heading` — на него ссылаются тесты и
  // CSS; меняется подпись, а не то, в какую корзину падает сет-лист
  // (правило `eventDate ?? createdAt` живёт в setlist-sort.ts и не тронуто).
  group('library.nextHeading', 'next-heading', upcoming, true);
  group('library.earlierHeading', 'past-heading', past, false);
}

/**
 * Songs panel — moved verbatim out of mountLibraryView in C5 (C7 adds alpha
 * sort). Keeps the data-team-id marker + song-body/row-slot/version-row/
 * viewers-btn hooks so navigation and owner affordances keep working
 * (`row-slot` replaced `audio-entry` in wave E — see buildSongEl).
 *
 * Wave C: the row source is `buildSongRows(team)` (song-rows.ts), not the old
 * `groupBySong(...).sort(...)` — a row exists for a song with words and zero
 * recordings too (Wave B's whole point), so the emptiness check below counts
 * ROWS, not `team.items`. A team with only words and no audio has
 * `team.items.length === 0` but is very much not empty.
 */
/** Persisted alongside 'bw.lib.tab'/'bw.lib.team' — the songs-panel sort choice. */
const SONG_SORT_KEY = 'bw.lib.songSort';

/** Read the persisted sort, defaulting to 'recent' (Sergey's decision: the
 * list sorts by last-opened-by-anyone by default). Same try/catch-wrapped
 * read as savedTeam/savedTab above — private-mode localStorage throws. */
function readSongSort(): SongSort {
  try {
    return localStorage.getItem(SONG_SORT_KEY) === 'alpha' ? 'alpha' : 'recent';
  } catch {
    return 'recent';
  }
}

function renderSongsPanel(panel: HTMLElement, team: TeamLibrary, opts: LibraryViewOpts): void {
  panel.innerHTML = '';
  const section = document.createElement('section');
  section.dataset.teamId = team.teamId;

  const allRows = buildSongRows(team);

  if (allRows.length === 0) {
    section.appendChild(buildEmptyState({ icon: 'musicOff', title: t('library.nothingPublished'), body: t('library.nothingPublishedBody'), dataRole: 'team-empty' }));
    panel.appendChild(section);
    return;
  }

  // Wave C completeness pass: rows exist (recordings may well exist too) but
  // NONE of them have words — distinct from allRows.length===0 above, where
  // the team has genuinely published nothing at all. Additive: the notice
  // sits above the list, which still renders normally below (the audio slot
  // keeps working; only the words-body tap and the words slot are disabled,
  // per row, as they already were — this explains why that's true for every
  // row at once).
  if (allRows.every((row) => row.words === null)) {
    section.appendChild(buildNoWordsNotice(opts));
  }

  let query = '';
  let sort: SongSort = readSongSort();
  const listEl = document.createElement('div');
  listEl.dataset.role = 'songs-list';

  // Search is always available in the Songs tab (Сергей 2026-07-02). The song
  // count moved to the tab label, so this bar is search-only.
  {
    const bar = document.createElement('div');
    bar.className = 'lib-songbar';
    const searchWrap = document.createElement('div');
    searchWrap.className = 'lib-search';
    searchWrap.innerHTML = icon('search');
    const input = document.createElement('input');
    input.dataset.role = 'songs-search';
    input.type = 'search';
    input.placeholder = t('library.searchPlaceholder');
    input.setAttribute('aria-label', t('library.searchPlaceholder'));
    input.addEventListener('input', () => { query = input.value; renderList(); });
    searchWrap.appendChild(input);
    bar.appendChild(searchWrap);
    section.appendChild(bar);
  }

  const renderList = (): void => {
    listEl.innerHTML = '';
    // Filter FIRST, then sort — the found set must read in the chosen order,
    // not just contain the right rows in whatever order they were built/matched.
    // (Verified: swapping this order is behaviorally unobservable for THIS pair
    // of pure functions — Array.prototype.sort is spec-guaranteed stable and
    // both comparators are row-intrinsic, so filter∘sort ≡ sort∘filter on the
    // resulting order. Filter-first is still correct to write: it is the
    // semantically right composition and sorts a smaller array.)
    const visible = sortSongRows(filterSongRows(allRows, query, getLocale()), sort, getLocale());
    if (visible.length === 0) {
      // Плашка — оболочка СПИСКА, и пустой она не бывает: «ничего не найдено»
      // не строка списка, а его отсутствие. В прототипе это отдельный блок
      // рядом с плашкой (`.state` при скрытом `#songs`), а не единственный
      // жилец внутри неё.
      listEl.className = '';
      const nm = document.createElement('p');
      nm.dataset.role = 'songs-no-match';
      nm.className = 'lib-nomatch';
      nm.textContent = t('library.songsNoMatch');
      listEl.appendChild(nm);
      return;
    }
    // Волна E, задача 3: список — ОДИН объект с волосяными разделителями между
    // соседями, а не россыпь карточек с зазором (прототип, `.plaque`).
    listEl.className = 'lib-plaque';
    for (const row of visible) listEl.appendChild(buildSongEl(row, team, opts, visible));
  };

  // Recent | A–Z switcher — persists like the tab/team choice above (same
  // try/catch-wrapped localStorage access; private mode must not crash the view).
  section.appendChild(buildSortControl({
    value: sort,
    labels: { recent: t('library.sortRecent'), alpha: t('library.sortAlpha'), caption: t('library.sortCaption') },
    onChange: (next) => {
      sort = next;
      try { localStorage.setItem(SONG_SORT_KEY, next); } catch { /* no localStorage */ }
      renderList();
    },
  }));

  section.appendChild(listEl);
  renderList();
  panel.appendChild(section);
}

/**
 * Owner "Viewers" control: an eye-icon button with a written label that
 * toggles an inline viewers-management panel. The active-viewer count is
 * fetched eagerly so it can show on the button; `placePanel` decides where the
 * opened panel is inserted (right after the version row it belongs to).
 *
 * ФОРМА ОДНА — та, что стоит ПОД СТРОКОЙ ВЕРСИИ в раскрытом списке: там кнопка
 * ни с чем не спорит за ширину, и подпись уместна.
 *
 * Волна E заводила вторую форму (`row-slot`: 44×44 без подписи, счётчик
 * бейджем в углу) для РЯДА СЛОТОВ строки песни. Волна F, задача 6 забрала у
 * неё место — в ряду теперь «⋯», открывающий лист управления песней, — и
 * форма осталась без единого вызывающего; задача 8 переселила список читателей
 * внутрь листа (ui/song-sheet-viewers.ts) и тем закрыла вопрос совсем. Форма
 * снята вместе со своим CSS и своими замками: неиспользуемый путь, который
 * держат тесты, хуже забытого — он выглядит живым.
 */
function buildViewersControl(
  shareId: string,
  teamId: string,
  opts: LibraryViewOpts,
  placePanel: (panel: HTMLDivElement) => void,
): HTMLButtonElement {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.dataset.role = 'viewers-btn';
  btn.dataset.shareId = shareId;
  btn.className = 'lib-viewers-btn';
  btn.innerHTML = icon('eye');
  const label = document.createElement('span');
  label.className = 'lib-viewers-label';
  label.textContent = t('library.viewersBtn');
  btn.appendChild(label);
  const countEl = document.createElement('span');
  countEl.className = 'lib-viewers-count';
  btn.appendChild(countEl);

  // Eager active-viewer count for the button badge (web-only; owned songs only).
  if (opts.loadViewers) {
    Promise.resolve(opts.loadViewers(shareId)).then((r) => {
      if (r && r.ok && Array.isArray(r.viewers)) {
        const n = r.viewers.filter((v) => v.revokedAt === null).length;
        if (n > 0) countEl.textContent = '· ' + n;
      }
    }, () => { /* leave the count blank on failure */ });
  }

  let vpanel: HTMLDivElement | null = null;
  btn.addEventListener('click', () => {
    if (vpanel) { vpanel.remove(); vpanel = null; }
    else { vpanel = buildViewersPanel(shareId, teamId, opts); placePanel(vpanel); }
  });
  return btn;
}

/**
 * Тихий «⋯» — ТРЕТИЙ слот ряда у песни, которую опубликовал смотрящий, и
 * единственный вход в лист управления ею (song-sheet.ts).
 *
 * Почему он заменил кнопку читателей. Волна E поставила её третьим слотом
 * ВРЕМЕННО: между волнами E и F владелец иначе остался бы без списка
 * читателей вовсе. Её место — в листе, и волна F её оттуда забирает. Форма при
 * этом сохранена дословно: та же клетка 44×44 (`lib-slot` — ряд обязан
 * остаться постоянной ширины, иначе имя песни снова начнёт обрезаться на
 * 375 px), приглушённый цвет (прототип, `.entry-more`, строка 326: настройки
 * трогают раз в месяц, поэтому орган не спорит с именем песни).
 *
 * Перевод и данные остаются ЗДЕСЬ: song-sheet.ts подписей не знает.
 */
function buildSongSheetControl(row: SongRow, team: TeamLibrary, opts: LibraryViewOpts): HTMLButtonElement {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.dataset.role = 'song-sheet-btn';
  // `lib-slot` — не «похожая» клетка, а буквально та же, что у слов и аудио:
  // габарит ряда задаётся одним правилом, и второй его источник означал бы,
  // что 44×44 можно потерять в одном месте и не заметить в другом.
  btn.className = 'lib-slot is-more';
  btn.innerHTML = icon('more');
  const song = row.songName || t('library.untitled');
  btn.setAttribute('aria-label', t('library.songSheetBtn'));
  btn.addEventListener('click', () => {
    const handle = openSongSheet({
      host: document.body,
      returnFocusTo: btn,
      labels: {
        title: song,
        dialogLabel: t('library.songSheetTitle', { song }),
        ownerNote: t('library.songSheetOwnerNote'),
        close: t('library.songSheetClose'),
      },
    });
    const words = buildWordsSection(row, opts, handle.close);
    if (words) handle.body.appendChild(words);
    const recordings = buildRecordingsSection(row, team, opts, handle.close);
    if (recordings) handle.body.appendChild(recordings);
    const viewers = buildViewersSection(row, opts);
    if (viewers) handle.body.appendChild(viewers);
  });
  return btn;
}

/**
 * Раздел «Слова» листа — или null, когда показывать нечего.
 *
 * Два условия отсутствия, и оба про ЧЕСТНОСТЬ, а не про удобство:
 *  · слов у песни нет — управлять нечем;
 *  · слова ЧУЖИЕ (опубликовал не смотрящий) — маршрут всё равно ответит 403
 *    `not_owner`, и показывать орган, обязанный отказать, нечестно. Раздела
 *    нет вовсе, а не гашёный: гашёный обещает, что когда-нибудь загорится.
 * Лист при этом остаётся: право на него шире (publishedByMe — своя может быть
 * запись, а не слова), и его остальные разделы к смотрящему относятся.
 *
 * Третье условие — проводка записи: без `setWordsVisibility`/`removeWords`
 * раздел нарисовал бы органы, которые молча ничего не делают. См. их
 * doc-комментарий в LibraryViewOpts.
 *
 * Перевод, формат даты и адрес ссылки собираются ЗДЕСЬ: song-sheet-words.ts
 * чист — ни `t()`, ни `location`, ни сети он не знает.
 */
function buildWordsSection(row: SongRow, opts: LibraryViewOpts, closeSheet: () => void): HTMLElement | null {
  const words = row.words;
  if (!words) return null;
  if (!opts.meEmail || words.ownerEmail !== opts.meEmail) return null;
  const { setWordsVisibility, removeWords } = opts;
  if (!setWordsVisibility || !removeWords) return null;

  // Сведение отсутствующего поля к «команде» — строго сравнением с 'public'
  // (см. SongTextSummary.visibility): проверка наоборот прочитала бы ответ
  // воркера, который про видимость ещё не знает, как «публично».
  const visibility = words.visibility === 'public' ? 'public' : 'team';

  // Тот же адрес, по которому ходит сама библиотека (`navigate('/words/' +
  // id)`), но АБСОЛЮТНЫЙ: его отправляют человеку, а не кликают.
  const linkUrl = `${location.origin}/words/${encodeURIComponent(words.id)}`;

  const sec = buildSongSheetWords({
    visibility,
    linkUrl,
    openedLabel: words.lastOpenedAt === null ? null : lastOpenedToken(words.lastOpenedAt, getLocale()),
    labels: {
      heading: t('library.wordsVisHeading'),
      team: t('library.wordsVisTeam'),
      public: t('library.wordsVisPublic'),
      explainTeam: t('library.wordsVisExplainTeam'),
      explainPublic: t('library.wordsVisExplainPublic'),
      linkLabel: t('library.wordsLinkLabel'),
      copy: t('library.wordsLinkCopy'),
      copied: t('library.wordsLinkCopied'),
      copyFailed: t('library.wordsLinkCopyFailed'),
      neverOpened: t('library.wordsNeverOpened'),
      removeBtn: t('library.wordsRemoveBtn'),
      removeWarning: t('library.wordsRemoveWarning'),
      removeConfirm: t('library.wordsRemoveConfirm'),
      removeCancel: t('library.wordsRemoveCancel'),
      errAuth: t('library.wordsErrAuth'),
      errForbidden: t('library.wordsErrForbidden'),
      errGone: t('library.wordsErrGone'),
      errNetwork: t('library.wordsErrNetwork'),
    },
    setVisibility: (next) => setWordsVisibility(words.id, next),
    removeWords: () => removeWords(words.id),
    // Копирование ОТВЕЧАЕТ, получилось ли (волна F, задача 8, C2): на
    // http-странице `navigator.clipboard` просто нет, и раздел писал
    // «Скопировано» там, где ничего не скопировалось. Полем фолбэка едет тот
    // самый адрес, что стоит рядом с кнопкой: `execCommand('copy')` копирует
    // выделение, и выделять ему нужно именно его.
    copyLink: (url) => copyText(url, sec?.querySelector<HTMLInputElement>('[data-role="words-link-url"]') ?? null),
    onRemoved: () => {
      // Лист закрывается сам: предмет, ради которого его открыли, только что
      // исчез, и оставленный открытым он показывал бы органы над пустотой.
      closeSheet();
      opts.onWordsRemoved?.();
    },
  });
  return sec;
}


/**
 * Записи ЭТОЙ песни, опубликованные смотрящим. Управлять можно только своим:
 * маршруты отвечают 403 `not_owner` на чужую запись, и показывать орган,
 * обязанный отказать, нечестно — то же правило, по которому раздел слов не
 * появляется у чужих слов.
 *
 * Без `meEmail` (аноним, или `/api/me` не ответил) своих записей не бывает ни
 * у одной песни — существующий замок, и ранний возврат его держит.
 */
function myVersions(row: SongRow, meEmail?: string | null): LibraryItem[] {
  if (!meEmail) return [];
  return row.versions.filter((v) => v.ownerEmail === meEmail);
}

/**
 * Раздел «Записи» листа — или null, когда показывать нечего.
 *
 * Условие отсутствия ОДНО и оно про предмет: своих записей у этой песни нет.
 * Раздела тогда нет ВОВСЕ, а не пустого с надписью «записей нет» — прототип
 * (`buildOwnerPanel`, секция «Which recording opens first») делает то же
 * самое. Лист при этом остаётся: право на него шире (своими могут быть слова),
 * и его остальные разделы к смотрящему относятся.
 *
 * Перевод и формат даты собираются ЗДЕСЬ: song-sheet-recordings.ts чист — ни
 * `t()`, ни сети он не знает.
 *
 * СНЯТИЕ ЗАПИСИ едет по cookie: `DELETE /api/shares/:id` научился второму
 * способу доказать право — сессии вместо пары `SHARE_TOKEN` + `X-User-Token`,
 * которой ходит десктоп (развилка по наличию заголовка `Authorization`, см.
 * `functions/api/shares/[shareId].ts`). До этого подать сюда было нечего, и
 * действие честно не рисовалось вовсе; теперь оно есть — но по-прежнему
 * ТОЛЬКО вместе с проводкой.
 */
function buildRecordingsSection(
  row: SongRow, team: TeamLibrary, opts: LibraryViewOpts, closeSheet: () => void,
): HTMLElement | null {
  const mine = myVersions(row, opts.meEmail);
  if (mine.length === 0) return null;

  const locale = getLocale();
  const { loadVisibility, setVisibility, removeVersion } = opts;

  return buildSongSheetRecordings({
    recordings: mine.map((v) => ({
      shareId: v.shareId,
      // Имя записи — её дата: другого имени у аудио-версии в библиотеке нет
      // (почта владельца здесь всегда одна и та же — смотрящего).
      name: formatShortDate(v.createdAt, locale),
      meta: recordingMeta(v),
      // Метка «главная» веб больше не читает (единый сетлист, фаза 4): голову
      // списка версий теперь везде решает свежесть записи, не выбор
      // владельца — `isSongDefault` (`shares.is_song_default`) остаётся в
      // ответе воркера (маршрут и колонка живут до фазы 5,
      // `TD-260924-song-default-route-until-phase-5`), но здесь его больше
      // никто не спрашивает. `setDefault` ниже не передаётся —
      // `buildSongSheetRecordings` сам гасит радио «главная» без него
      // (song-sheet-recordings.ts, `pickable`), оставляя видимость и снятие
      // записи на месте.
      isDefault: false,
    })),
    labels: {
      heading: t('library.recordingsHeading'),
      defaultGroupLabel: t('library.recordingsDefaultLabel'),
      defaultBadge: t('library.recordingsDefaultBadge'),
      visLabel: t('library.recordingsVisLabel'),
      visTeam: t('library.recordingsVisTeam'),
      visPublic: t('library.recordingsVisPublic'),
      visError: t('library.recordingsVisError'),
      visReading: t('library.recordingsVisReading'),
      visUnknown: t('library.recordingsVisUnknown'),
      removeBtn: t('library.recordingsRemoveBtn'),
      removeWarning: t('library.recordingsRemoveWarning'),
      removeConfirm: t('library.recordingsRemoveConfirm'),
      removeCancel: t('library.recordingsRemoveCancel'),
      errAuth: t('library.recordingsErrAuth'),
      errForbidden: t('library.recordingsErrForbidden'),
      errGone: t('library.recordingsErrGone'),
      errRevoked: t('library.recordingsErrRevoked'),
      errNoSong: t('library.recordingsErrNoSong'),
      errNetwork: t('library.recordingsErrNetwork'),
    },
    ...(loadVisibility ? { loadVisibility: (shareId: string) => loadVisibility(shareId) } : {}),
    // Видимость едет по СУЩЕСТВУЮЩЕМУ пути — тому же `setVisibility`, которым
    // пользуется панель читателей. Раздел даёт ему второй ВХОД, а не второй
    // путь; `teamId` подставляется здесь, потому что команду знает вьюха, а не
    // чистый модуль.
    ...(setVisibility
      ? { setVisibility: (shareId: string, next: 'team' | 'public') => setVisibility(shareId, next, team.teamId) }
      : {}),
    ...(removeVersion
      ? {
        removeRecording: (shareId: string) => removeVersion(shareId),
        onRemoved: () => {
          // Лист закрывается сам — тем же приёмом, что у снятия слов: запись,
          // ради которой открыт его раздел, только что исчезла, и оставленный
          // открытым лист показывал бы органы над пустотой (а список под ним
          // вызывающий сейчас перемонтирует).
          closeSheet();
          opts.onVersionRemoved?.();
        },
      }
      : {}),
  });
}

/**
 * Метастрока записи — «added 2 days ago» прототипа, и `null`, когда сказать
 * нечего.
 *
 * ЧИСЛА ДОРОЖЕК здесь нет, и это не забывчивость: прототип показывает
 * «12 tracks · added 2 days ago», а `/api/library` числа дорожек не отдаёт
 * вовсе (`LibraryItem` его не несёт — см. library.ts). Выдумать его нечем, а
 * показать неверное хуже, чем не показать: мета несёт то, что есть.
 * Относительной подсказки у старой записи нет — дата уже стоит именем строки,
 * и мета тогда пропадает целиком, а не выводит пустой разделитель.
 */
function recordingMeta(v: LibraryItem): string | null {
  const hint = relativeHint(v.createdAt, new Date());
  if (!hint) return null;
  return t('library.recordingsAdded', { when: t(hint.key, hint.n != null ? { n: hint.n } : {}) });
}

/**
 * Раздел «Кто открывал» листа — или null, когда показывать нечего.
 *
 * ТОЛЬКО ДЛЯ ЗАПИСЕЙ. У слов имён нет и не будет: решение схемы 0018 дословно
 * — «кто открыл не пишется и не показывается, для команды из четырёх человек
 * это слежка», и раздел слов его соблюдает (показывает одно время). Имена есть
 * у аудио-записи, потому что доступ к ней раздаётся и отзывается ПОИМЁННО.
 * Поэтому условие отсутствия то же, что у раздела записей: своих записей у
 * этой песни нет — раздела нет вовсе.
 *
 * ЧИТАТЕЛИ ПЕСНИ, А НЕ ОДНОЙ ЗАПИСИ. Лист управляет песней, у которой бывает
 * три записи, и `listViewers` спрашивает по одной. Списки сливаются по
 * `userId`, отозванные отбрасываются, а отзыв уходит по КАЖДОЙ своей записи,
 * где человек числится: доступ, отнятый у одной и оставленный у другой, — не
 * «убрал», а «убрал наполовину».
 */
function buildViewersSection(row: SongRow, opts: LibraryViewOpts): HTMLElement | null {
  const mine = myVersions(row, opts.meEmail);
  if (mine.length === 0) return null;
  const { loadViewers, revokeViewer } = opts;
  if (!loadViewers) return null;

  /** userId → мои записи, где он числится. Заполняется КАЖДОЙ загрузкой, и
   *  отзыв читает именно её: адреса берутся из ответа, а не угадываются. */
  const whereSeen = new Map<string, string[]>();

  return buildSongSheetViewers({
    labels: {
      heading: t('library.sheetViewersHeading'),
      empty: t('library.sheetViewersEmpty'),
      error: t('library.sheetViewersError'),
      revoke: t('library.sheetViewersRevoke'),
      teamNote: t('library.sheetViewersTeamNote'),
      revokeError: t('library.sheetViewersRevokeError'),
    },
    load: () => Promise.all(mine.map((v) =>
      Promise.resolve(loadViewers(v.shareId)).then(
        (r) => (r && r.ok ? { shareId: v.shareId, viewers: r.viewers } : null),
        () => null,
      ),
    )).then((results) => mergeSongViewers(results, whereSeen, getLocale())),
    ...(revokeViewer
      ? {
        revoke: (userId: string) => {
          const shares = whereSeen.get(userId) ?? [];
          if (shares.length === 0) return Promise.resolve(false);
          return Promise.all(shares.map((shareId) => Promise.resolve(revokeViewer(shareId, userId))))
            .then((rs) => rs.every((r) => r.ok), () => false);
        },
      }
      : {}),
  });
}

/**
 * Слить читателей нескольких записей одной песни в один список.
 *
 * `null` возвращается, только когда НИ ОДНА запись не прочиталась: «не
 * прочиталось» и «никого нет» — разные вещи, и вторая на месте первой читалась
 * бы как «вас никто не открывал». Частичный успех (одна запись ответила,
 * другая нет) остаётся списком: показать то, что удалось узнать, честнее, чем
 * спрятать всё из-за одной осечки.
 *
 * Отозванные пропускаются: раздел отвечает на «кто СЕЙЧАС имеет доступ и
 * открывал», а не ведёт архив.
 *
 * `team` побеждает `grant` при слиянии, и это не мелочь: у участника команды
 * доступ остаётся через команду, даже если по одной записи он числится
 * приглашённым по ссылке. Нарисовать ему кнопку отзыва значило бы пообещать
 * то, чего она не сделает.
 */
function mergeSongViewers(
  results: Array<{ shareId: string; viewers: ViewerInfo[] } | null>,
  whereSeen: Map<string, string[]>,
  locale: string,
): SongSheetViewer[] | null {
  if (results.every((r) => r === null)) return null;

  whereSeen.clear();
  const byUser = new Map<string, { email: string; lastSeenAt: string | null; viewCount: number; source: 'team' | 'grant' }>();
  const order: string[] = [];

  for (const r of results) {
    if (!r) continue;
    for (const v of r.viewers) {
      if (v.revokedAt !== null) continue;
      whereSeen.set(v.userId, [...(whereSeen.get(v.userId) ?? []), r.shareId]);
      const prev = byUser.get(v.userId);
      if (!prev) {
        order.push(v.userId);
        byUser.set(v.userId, {
          email: v.email,
          lastSeenAt: v.lastSeenAt,
          viewCount: v.viewCount,
          source: v.source,
        });
        continue;
      }
      // Свежайшее открытие и суммарное число заходов: раздел про ПЕСНЮ, а не
      // про одну её запись.
      if (v.lastSeenAt !== null && (prev.lastSeenAt === null || v.lastSeenAt > prev.lastSeenAt)) {
        prev.lastSeenAt = v.lastSeenAt;
      }
      prev.viewCount += v.viewCount;
      if (v.source === 'team') prev.source = 'team';
    }
  }

  return order.map((userId) => {
    const v = byUser.get(userId)!;
    return { userId, email: v.email, seen: viewerSeenToken(v, locale), source: v.source };
  });
}

/**
 * «Opened yesterday · 3 times · by link» — строка под почтой (прототип,
 * `seenText`). Отсутствующий токен ПРОПУСКАЕТСЯ, а не превращается в пустой,
 * поэтому разделитель никогда не удваивается.
 *
 * Ни разу не открывал → сказано словом, и числа заходов рядом нет: «0 times»
 * не факт, а шум — это то же самое, сказанное второй раз.
 */
function viewerSeenToken(
  v: { lastSeenAt: string | null; viewCount: number; source: 'team' | 'grant' },
  locale: string,
): string {
  const tokens: string[] = [];
  if (v.lastSeenAt === null) {
    tokens.push(t('library.sheetViewersNever'));
  } else {
    tokens.push(lastOpenedToken(v.lastSeenAt, locale));
    tokens.push(tn('library.sheetViewersTimes', v.viewCount));
  }
  if (v.source === 'grant') tokens.push(t('library.sheetViewersByLink'));
  return tokens.join(' · ');
}

/**
 * Кому принадлежит ПЕСНЯ настолько, чтобы ею управлять: смотрящий опубликовал
 * хоть что-то про неё — слова или любую из записей.
 *
 * Волна F, задача 7 — это ПОЧИНКА, а не расширение. До неё право считалось
 * условием волны E (`count === 1 && latest.ownerEmail === meEmail`), где орган
 * открывал панель читателей ОДНОЙ аудио-записи и потому справедливо смотрел на
 * одну запись. Лист управления ПЕСНЕЙ то же условие сужает ровно там, где ему
 * есть что показать:
 *   · `count === 1` вырезал многоверсионную песню — единственную, у которой
 *     раздел «какая запись открывается первой» вообще имеет предмет;
 *   · `latest` требовал хотя бы одной записи — то есть вырезал песню только со
 *     словами, которой адресован весь раздел слов.
 * Лист появлялся ровно там, где ему меньше всего что показывать.
 *
 * Считается по почте СМОТРЯЩЕГО и только по ней: без `meEmail` (аноним, или
 * `/api/me` не ответил) листа нет ни у одной песни — существующий замок, и
 * ранний возврат ниже его держит. Серверная сторона проверяет то же самое
 * заново (PATCH/DELETE отвечают 403 `not_owner`), так что это условие решает,
 * ЧТО ПОКАЗАТЬ, а не кому можно.
 *
 * `versions.some`, а не `versions[0]`: у песни бывает три записи от разных
 * людей, и своя из них может быть не первой.
 */
function publishedByMe(row: SongRow, meEmail?: string | null): boolean {
  if (!meEmail) return false;
  if (row.words?.ownerEmail === meEmail) return true;
  return row.versions.some((v) => v.ownerEmail === meEmail);
}

/**
 * Row meta — at most three tokens (song key · versions-or-no-words · last
 * opened), joined with ' · '. An absent token is simply skipped, never an
 * empty string, so the join never produces a doubled separator.
 *
 * The second token is about WORDS, not raw audio count: `words === null`
 * means the body is the disabled state below, so the meta line echoes the
 * same reason inline ("no words") instead of a version count. The count only
 * appears above 1, so a single-version song never reads "1 versions" (the
 * bare plural read badly, and a lone version needs no count to announce
 * itself: the audio slot already says a recording exists). When words exist
 * but versions is empty (a words-only leftover row — never happens for
 * words === null, since every audio-group row has ≥1 version by
 * construction) the token is skipped outright rather than showing "0
 * versions".
 *
 * Wave E note: this used to read "just repeat what the audio-entry pill
 * already shows" — that pill (`[data-role="version-pill"]`, "N versions" on
 * the audio button) left the row with the rest of the wide right-hand side.
 * The meta token is now the ONLY place a version count appears, and the
 * `words === null` branch above still swallows it — so a multi-version song
 * with no words published shows no count anywhere. Deliberate for now (the
 * expanding audio slot still lists every version), flagged in the wave E
 * report rather than fixed silently here.
 */
function songMetaTokens(row: SongRow, locale: string): string[] {
  const tokens: string[] = [];
  if (row.songKey) tokens.push(row.songKey);
  if (row.words === null) {
    tokens.push(t('library.noWords'));
  } else if (row.versions.length > 1) {
    tokens.push(tn('library.songVersions', row.versions.length));
  }
  if (row.lastOpenedAt !== null) tokens.push(lastOpenedToken(row.lastOpenedAt, locale));
  return tokens;
}

/** "Opened {relative-or-short-date}" — reuses the same rel-hint keys as setlist rows. */
function lastOpenedToken(iso: string, locale: string): string {
  const hint = relativeHint(iso, new Date());
  const when = hint ? t(hint.key, hint.n != null ? { n: hint.n } : {}) : formatShortDate(iso, locale);
  return t('library.wordsOpened', { when });
}

/**
 * Build one song row: a `.lib-card-row` holding the body
 * `[data-role="song-body"]` (name + meta; opens the words page) and, to its
 * right, the row's PAIR of controls — always exactly two slots, built by
 * `buildSongRowControls` (song-row-controls.ts). Sergey's decisions (locked):
 * the row body opens WORDS, audio needs an explicit separate control, and
 * both stay — so the two must never share a click handler or nest one inside
 * the other.
 *
 * No words (`row.words === null`) → the body is non-clickable: no navigate
 * listener attached, `aria-disabled="true"`, and a `title` hint explaining
 * why (an unexplained disabled control is treated as a defect in this
 * project — see app/renderer/ui/duplicate-menu-item.js).
 *
 * Absent surfaces and the PAIR of slots. Waves C/D held the opposite rule
 * here: "нет аудио → кнопки нет вовсе, не гашёная" (Сергей) — no audio meant
 * no control in the DOM at all, on the reasoning that there is nothing to
 * explain when the recordings simply don't exist yet. **Wave E reversed that
 * decision**, per the prototype approved on 18.09: the right-hand side is a
 * COLUMN read top-to-bottom, and a control that appears and disappears from
 * row to row makes that column zig-zag — the eye loses the line before it
 * reaches the song it came for. So the slot now always stands its ground and
 * NAMES the absence (`title`/`aria-label`) instead of vanishing. The old
 * reasoning wasn't wrong about a single row in isolation; it was answering a
 * question about one row while the defect lived in the column.
 *
 * Owner "Viewers" affordance (задача 2 волны). Спека про пилюлю владельца:
 * «в волне E она просто перестаёт конкурировать за ширину с именем — уезжает
 * в тот же ряд иконок». Поэтому у песни с ОДНОЙ версией она теперь третий
 * слот внутри `.lib-row-controls`, а не отдельный широкий элемент строки.
 * В развёрнутом списке версий (count > 1) она осталась прежней: стоит под
 * строкой версии, ни с чем за ширину не спорит, подпись там уместна. Волна F,
 * задача 8 сняла компактную форму совсем — у неё не осталось вызывающего.
 */
function buildSongEl(row: SongRow, team: TeamLibrary, opts: LibraryViewOpts, visible: SongRow[]): HTMLElement {
  const songEl = document.createElement('div');
  songEl.dataset.songId = row.key;
  songEl.className = 'lib-song';

  const hasWords = row.words !== null;
  const count = row.versions.length;
  const hasAudio = count > 0;

  const cardRow = document.createElement('div');
  cardRow.dataset.role = 'song-row';
  cardRow.className = 'lib-card-row';

  // ── Body: name + meta, opens the words page (Сергей: "просто склацаешь по
  // песне — открываются слова"). ──
  const body = document.createElement('button');
  body.dataset.role = 'song-body';
  body.className = 'song-card song-body';

  const main = document.createElement('span');
  main.className = 'song-main';
  const nameEl = document.createElement('span');
  nameEl.className = 'song-name';
  nameEl.textContent = row.songName || t('library.untitled');
  main.appendChild(nameEl);

  const tokens = songMetaTokens(row, getLocale());
  if (tokens.length > 0) {
    const metaEl = document.createElement('span');
    metaEl.dataset.role = 'song-meta';
    metaEl.className = 'song-sub';
    metaEl.textContent = tokens.join(' · ');
    main.appendChild(metaEl);
  }
  body.appendChild(main);

  if (hasWords) {
    // Соседи страницы слов — снимок ИМЕННО этого рендера списка (уже
    // отсортированного и отфильтрованного): стрелка ‹› там обязана вести
    // туда, куда только что вёл глаз здесь. Кладётся ПЕРЕД навигацией — сама
    // страница слов её читает и сразу съедает (song-words-view.ts).
    // saveScrollTarget — тем же приёмом (point 7): «куда вернуться» в
    // библиотеке съедает mountLibraryView при следующем монтировании.
    body.addEventListener('click', () => {
      saveWordsNeighbors(neighborsFromRows(visible));
      saveScrollTarget(row.key);
      // Дефект 4 живой приёмки волны G: крестик на странице слов обязан
      // вернуть СЮДА, на вкладку Songs — не хардкодом в song-words-view.ts,
      // а по этой записи (readWordsOrigin читает и съедает её на монтаже).
      saveWordsOrigin({ path: '/library', tab: 'songs' });
      opts.navigate('/words/' + row.words!.id);
    });
  } else {
    body.setAttribute('aria-disabled', 'true');
    body.classList.add('is-disabled');
    body.title = t('library.wordsNotPublished');
  }
  cardRow.appendChild(body);

  // ── Правая часть строки — пара слотов фиксированной ширины
  // (song-row-controls.ts). Раскрытие списка версий остаётся поведением слота
  // аудио у многоверсионной песни: орган сменил форму, не назначение. ──
  const latest = row.versions[0];
  let versionsWrap: HTMLDivElement | null = null;
  if (count > 1) {
    versionsWrap = document.createElement('div');
    versionsWrap.dataset.role = 'versions';
    // id — чтобы слот аудио мог НАЗВАТЬ то, чем управляет (`aria-controls`).
    // Счётчик, а не `row.key`: ключ приходит из данных и может встретиться
    // дважды на экране при двух командах в одном DOM, а id обязан быть один.
    versionsWrap.id = 'lib-versions-' + (++versionsSeq);
    versionsWrap.hidden = true;
    for (const v of row.versions) {
      const vrow = document.createElement('button');
      vrow.dataset.role = 'version-row';
      vrow.dataset.shareId = v.shareId;
      vrow.textContent = [formatShortDate(v.createdAt, getLocale()), v.ownerEmail].filter(Boolean).join(' · ');
      vrow.addEventListener('click', () => opts.navigate('/' + v.shareId));
      versionsWrap.appendChild(vrow);

      // Owner affordance for multi-version songs: viewers control after each version row
      if (opts.meEmail && v.ownerEmail === opts.meEmail) {
        const vbtn = buildViewersControl(v.shareId, team.teamId, opts, (p) => vbtn.insertAdjacentElement('afterend', p));
        versionsWrap.appendChild(vbtn);
      }
    }
  }
  const wrap = versionsWrap;
  const controls = buildSongRowControls({
    hasWords,
    hasAudio,
    // Раскрывашкой слот становится ровно тогда, когда есть что раскрывать:
    // список версий существует только при count > 1.
    audioExpands: wrap !== null,
    ...(wrap ? { audioControls: wrap.id } : {}),
    labels: {
      words: t('library.wordsEntry'),
      audio: t('library.audioEntry'),
      audioVersions: t('library.audioVersions'),
      noWords: t('library.noWordsSlot'),
      noAudio: t('library.noAudioSlot'),
    },
    onWords: () => {
      saveWordsNeighbors(neighborsFromRows(visible));
      saveScrollTarget(row.key);
      // Дефект 4 живой приёмки волны G — см. пояснение у body-клика выше.
      saveWordsOrigin({ path: '/library', tab: 'songs' });
      opts.navigate('/words/' + row.words!.id);
    },
    onAudio: () => {
      // Возврат — не украшение: раскрывашка отражает состояние СПИСКА, и
      // владелец списка здесь. Снять return значит оставить `aria-expanded`
      // враньём.
      if (wrap) { wrap.hidden = !wrap.hidden; return !wrap.hidden; }
      if (latest) opts.navigate('/' + latest.shareId);
    },
  });
  cardRow.appendChild(controls);

  if (publishedByMe(row, opts.meEmail)) {
    // Волна E, задача 2 поставила сюда ТРЕТИЙ слот ряда — орган владельца.
    // Волна F, задача 6 поменяла, ЧТО он открывает (лист управления песней
    // вместо панели читателей одной записи), а задача 7 — КОМУ он показан:
    // см. publishedByMe выше, условие волны E было прямым дефектом.
    controls.appendChild(buildSongSheetControl(row, team, opts));
  }

  songEl.appendChild(cardRow);
  if (versionsWrap) songEl.appendChild(versionsWrap);

  return songEl;
}
